import { Props } from 'props';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { IRiskMatrixViewModel } from 'viewModels';
import { useAuth } from './auth';
import { useInfiniteQuery } from 'react-query';
import { RiskMatrixController } from 'controllers/RiskMatrixController';
import {
  IOrderFieldInputModel,
  IRiskMatrixInputModel,
} from 'inputModels';
import { Columns, FilterProps } from 'props';
import { notification } from 'antd';

interface RiskMatrixContextProps {
  riskMatrices: IRiskMatrixViewModel[];
  setRiskMatrices: (value: IRiskMatrixViewModel[]) => void;
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  refetch: () => void;
  orderBy: IOrderFieldInputModel<Columns>[];
  setOrder: React.Dispatch<
    React.SetStateAction<IOrderFieldInputModel<Columns>[]>
  >;
  filter: FilterProps | undefined;
  setFilter: React.Dispatch<FilterProps | undefined>;
  createRiskMatrix: (input: IRiskMatrixInputModel) => Promise<void>;
  updateRiskMatrix: (input: IRiskMatrixInputModel) => Promise<void>;
  deleteRiskMatrix: (id: string) => Promise<void>;
  updateRiskMatrixStatus: (id: string) => Promise<void>;
}

const RiskMatrixContext = createContext<RiskMatrixContextProps>(
  {} as RiskMatrixContextProps
);

const RiskMatrixProvider: React.FC<Props> = ({ children }) => {
  const { logged, user } = useAuth();
  const [orderBy, setOrder] = useState<
    IOrderFieldInputModel<Columns>[]
  >([]);
  const [filter, setFilter] = useState<FilterProps>();
  const [riskMatrices, setRiskMatrices] = useState<
    IRiskMatrixViewModel[]
  >([]);
  const PAGE_SIZE = 10;

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ['riskMatrices', logged],
    ({ pageParam = 1 }) =>
      RiskMatrixController.getRiskMatrices<Columns>({
        orderBy,
        page: pageParam,
        take: PAGE_SIZE,
        term: filter?.term,
        filter: filter?.term ? filter?.filter : undefined,
      }).then((res) => res.data),
    {
      enabled: !!user?.id && logged,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    }
  );

  useEffect(() => {
    if (data) {
      setRiskMatrices(data.pages.flat());
    }
  }, [data]);

  useEffect(() => {
    if (!logged) {
      setRiskMatrices([]);
    }
  }, [logged]);

  const createRiskMatrix = async (input: IRiskMatrixInputModel) => {
    try {
      const response = await RiskMatrixController.createRiskMatrix(
        input
      );
      const newMatrix: IRiskMatrixViewModel = {
        id: response.data,
        document: input.document,
        name: input.name,
        risks: input.risks,
        createdAt: new Date().toISOString(),
        createdBy: user?.id || '',
        disabled: false,
      };
      setRiskMatrices((prev) => [...prev, newMatrix]);
      refetch();
      notification.success({
        message: 'Matriz criada',
        description: 'A matriz de risco foi criada com sucesso.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: 'Erro ao criar matriz',
        description: 'Ocorreu um erro ao criar a matriz de risco.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    }
  };

  const updateRiskMatrix = async (input: IRiskMatrixInputModel) => {
    try {
      await RiskMatrixController.updateRiskMatrix(input);
      setRiskMatrices((prev) =>
        prev.map((matrix) =>
          matrix.id === input.id
            ? { ...matrix, ...input, id: input.id }
            : matrix
        )
      );
      refetch();
      notification.success({
        message: 'Matriz atualizada',
        description: 'A matriz de risco foi atualizada com sucesso.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: 'Erro ao atualizar matriz',
        description:
          'Ocorreu um erro ao atualizar a matriz de risco.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    }
  };

  const deleteRiskMatrix = async (id: string) => {
    try {
      await RiskMatrixController.deleteRiskMatrix(id);
      setRiskMatrices((prev) =>
        prev.filter((matrix) => matrix.id !== id)
      );
      refetch();
      notification.success({
        message: 'Matriz excluída',
        description: 'A matriz de risco foi excluída com sucesso.',
        placement: 'topRight',
        pauseOnHover: true,
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: 'Erro ao excluir matriz',
        description: 'Ocorreu um erro ao excluir a matriz de risco.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    }
  };

  const updateRiskMatrixStatus = async (id: string) => {
    try {
      await RiskMatrixController.updateRiskMatrixStatus(id);
      setRiskMatrices((prev) =>
        prev.map((matrix) =>
          matrix.id === id
            ? { ...matrix, disabled: !matrix.disabled }
            : matrix
        )
      );
      refetch();
      notification.success({
        message: 'Status atualizado',
        description:
          'O status da matriz de risco foi atualizado com sucesso.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    } catch (error) {
      notification.error({
        message: 'Erro ao atualizar status',
        description:
          'Ocorreu um erro ao atualizar o status da matriz de risco.',
        placement: 'topRight',
        pauseOnHover: true,
        showProgress: true,
        duration: 3,
      });
    }
  };

  return (
    <RiskMatrixContext.Provider
      value={{
        riskMatrices,
        setRiskMatrices,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch,
        orderBy,
        setOrder,
        filter,
        setFilter,
        createRiskMatrix,
        updateRiskMatrix,
        deleteRiskMatrix,
        updateRiskMatrixStatus,
      }}
    >
      {children}
    </RiskMatrixContext.Provider>
  );
};

export const useRiskMatrix: () => RiskMatrixContextProps = () =>
  useContext(RiskMatrixContext);

export default RiskMatrixProvider;
